import React, { useEffect, useState } from 'react';
import {
  Paper,
  Grid,
  Box,
  Stack,
  Typography,
  Divider,
  Card,
  CardContent,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  Inventory,
  AccountBalance,
  LocalOffer,
} from '@mui/icons-material';
import api from '../services/api';

// Helper to format month from date range
const getMonthLabel = (dateRange) => {
  if (!dateRange?.start) return '';
  const date = new Date(dateRange.start);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

// Helper to format date range
const formatDateRange = (dateRange) => {
  if (!dateRange?.start || !dateRange?.end) return '';
  const s = new Date(dateRange.start);
  const e = new Date(dateRange.end);
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${s.toLocaleDateString('en-KE', opts)} - ${e.toLocaleDateString('en-KE', opts)}`;
};

// Format currency
const formatCurrency = (v) => `KES ${Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Format date
const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-KE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

// Print page header component
const PrintPageHeader = ({ settings, reportTitle, dateRange, reportId, branch }) => {
  const companyName = settings?.clinic_name || 'NutriAxis Nutrition Consultancy & Wellness Clinic';
  const companyAddress = settings?.clinic_address || 'Nairobi, Kenya';
  const companyPhone = settings?.clinic_phone || '+254 700 000000';
  const companyEmail = settings?.clinic_email || 'info@nutriaxis.com';
  const companyWebsite = settings?.clinic_website || 'www.nutriaxis.com';

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 3, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        pageBreakAfter: 'always',
        pageBreakInside: 'avoid',
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Left: Company Logo and Details */}
        <Grid item xs={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar 
              src="/nutriaxis-logo.png" 
              alt="Company Logo"
              sx={{ 
                width: 64, 
                height: 64,
                bgcolor: 'primary.light',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              N
            </Avatar>
            <Stack>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {companyAddress}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tel: {companyPhone} | Email: {companyEmail}
              </Typography>
              {companyWebsite && (
                <Typography variant="body2" color="text.secondary">
                  Web: {companyWebsite}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>

        {/* Center: Report Title */}
        <Grid item xs={4} sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
            {reportTitle}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Period: {getMonthLabel(dateRange) ? `The Month Of ${getMonthLabel(dateRange)}` : formatDateRange(dateRange)}
          </Typography>
        </Grid>

        {/* Right: Report Metadata */}
        <Grid item xs={4}>
          <Stack spacing={0.5} sx={{ textAlign: 'right' }}>
            <Typography variant="body2">
              <strong>Generated By:</strong> System
            </Typography>
            <Typography variant="body2">
              <strong>Generated Date:</strong> {new Date().toLocaleDateString('en-KE', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
            </Typography>
            {reportId && (
              <Typography variant="body2">
                <strong>Report ID:</strong> {reportId}
              </Typography>
            )}
            {branch && (
              <Typography variant="body2">
                <strong>Branch:</strong> {branch}
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Print page footer component
const PrintPageFooter = ({ reportType = 'sales' }) => {
  const reportName = reportType === 'sales' ? 'Sales Report' : 
                     reportType === 'inventory' ? 'Inventory Report' : 
                     reportType === 'financial' ? 'Financial Report' : 'Client Report';
  
  return (
    <Box 
      sx={{ 
        mt: 4, 
        pt: 2, 
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        NutriAxis Nutrition Consultancy & Wellness Clinic
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {reportName} | Confidential
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Page <span className="page-number"></span> of <span className="total-pages"></span>
      </Typography>
    </Box>
  );
};

// KPI Card for print
const PrintKpiCard = ({ label, value, icon, highlight = false }) => {
  return (
    <Card 
      elevation={0}
      sx={{ 
        border: '1px solid',
        borderColor: highlight ? 'primary.main' : 'divider',
        bgcolor: highlight ? 'primary.50' : 'background.paper',
        borderRadius: 1,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {icon && React.cloneElement(icon, { fontSize: 'small', color: highlight ? 'primary' : 'action' })}
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            {label}
          </Typography>
        </Stack>
        <Typography variant="h6" fontWeight="bold" color={highlight ? 'primary.main' : 'text.primary'}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Chart container for print
const PrintChartContainer = ({ title, children }) => {
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        {children}
      </Box>
    </Paper>
  );
};

// Main ReportPrintLayout component
const ReportPrintLayout = ({ 
  reportData, 
  dateRange, 
  reportType = 'sales',
  onPrint,
  onExportPdf,
}) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings');
        setSettings(res.data?.settings || {});
      } catch (e) {
        setSettings({
          clinic_name: 'NutriAxis Nutrition Consultancy & Wellness Clinic',
          clinic_address: 'Nairobi, Kenya',
          clinic_phone: '+254 700 000000',
          clinic_email: 'info@nutriaxis.com',
          clinic_website: 'www.nutriaxis.com'
        });
      }
    };
    fetchSettings();
  }, []);

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  const handleExportPdf = () => {
    if (onExportPdf) {
      onExportPdf();
    } else {
      window.print();
    }
  };

  // Generate report ID
  const reportId = `RPT-${reportType.toUpperCase()}-${Date.now().toString().slice(-6)}`;

  return (
    <Box className="print-layout-container">
      {/* Screen-only controls */}
      <Box className="print-controls no-print" sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <button 
          onClick={handlePrint}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Print Report
        </button>
        <button 
          onClick={handleExportPdf}
          style={{
            padding: '8px 16px',
            backgroundColor: '#dc004e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Export to PDF
        </button>
      </Box>

      {/* Print document */}
      <Box className="print-document">
        <PrintPageHeader 
          settings={settings}
          reportTitle={reportType === 'sales' ? 'Sales Report' : 
                      reportType === 'inventory' ? 'Inventory Report' : 
                      reportType === 'financial' ? 'Financial Report' : 'Client Report'}
          dateRange={dateRange}
          reportId={reportId}
          branch={settings?.branch_name || 'Main Branch'}
        />

        <main>
          {reportData ? (
            <>
              {/* Executive Summary - KPI Cards */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Executive Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {reportType === 'sales' && (
                    <>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Total Sales" 
                          value={formatCurrency(reportData.summary?.total_sales)} 
                          icon={<AttachMoney />}
                          highlight 
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Gross Revenue" 
                          value={formatCurrency(reportData.summary?.gross_revenue || reportData.summary?.total_sales)} 
                          icon={<AttachMoney />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Net Revenue" 
                          value={formatCurrency(reportData.summary?.net_revenue || reportData.summary?.total_sales)} 
                          icon={<AttachMoney />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Total Transactions" 
                          value={(reportData.summary?.total_transactions || 0).toLocaleString()} 
                          icon={<Receipt />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Average Sale" 
                          value={formatCurrency(reportData.summary?.avg_sale_value)} 
                          icon={<BarChartIcon />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Profit" 
                          value={formatCurrency(reportData.summary?.profit || 0)} 
                          icon={<TrendingUp />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Expenses" 
                          value={formatCurrency(reportData.summary?.expenses || 0)} 
                          icon={<TrendingDown />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Inventory Value" 
                          value={formatCurrency(reportData.summary?.inventory_value || 0)} 
                          icon={<Inventory />}
                        />
                      </Grid>
                    </>
                  )}
                  {reportType === 'inventory' && (
                    <>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Total Stock Items" 
                          value={(reportData.summary?.total_items || 0).toLocaleString()} 
                          icon={<Inventory />}
                          highlight 
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Inventory Value" 
                          value={formatCurrency(reportData.summary?.inventory_value)} 
                          icon={<AttachMoney />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Low Stock Items" 
                          value={(reportData.summary?.low_stock_count || 0).toLocaleString()} 
                          icon={<LocalOffer />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Out of Stock" 
                          value={(reportData.summary?.out_of_stock_count || 0).toLocaleString()} 
                          icon={<LocalOffer />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Best Selling Products" 
                          value={(reportData.summary?.best_selling_count || 0).toLocaleString()} 
                          icon={<TrendingUp />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Slow Moving Products" 
                          value={(reportData.summary?.slow_moving_count || 0).toLocaleString()} 
                          icon={<TrendingDown />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Stock Adjustments" 
                          value={(reportData.summary?.adjustments_count || 0).toLocaleString()} 
                          icon={<Receipt />}
                        />
                      </Grid>
                    </>
                  )}
                  {reportType === 'financial' && (
                    <>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Revenue" 
                          value={formatCurrency(reportData.summary?.total_revenue)} 
                          icon={<AttachMoney />}
                          highlight 
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Cost of Goods Sold" 
                          value={formatCurrency(reportData.summary?.cogs || 0)} 
                          icon={<Receipt />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Gross Profit" 
                          value={formatCurrency(reportData.summary?.gross_profit || 0)} 
                          icon={<TrendingUp />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Operating Expenses" 
                          value={formatCurrency(reportData.summary?.operating_expenses || 0)} 
                          icon={<TrendingDown />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Net Profit" 
                          value={formatCurrency(reportData.summary?.net_profit || 0)} 
                          icon={<AccountBalance />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Profit Margin" 
                          value={`${reportData.summary?.profit_margin ? Number(reportData.summary.profit_margin).toFixed(1) : '0'}%`} 
                          icon={<BarChartIcon />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="VAT Collected" 
                          value={formatCurrency(reportData.summary?.vat_collected || 0)} 
                          icon={<LocalOffer />}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <PrintKpiCard 
                          label="Refunds" 
                          value={formatCurrency(reportData.summary?.refunds || 0)} 
                          icon={<Receipt />}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>

              {/* Sales Analysis Section */}
              {reportType === 'sales' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Sales Analysis
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <PrintChartContainer title="Daily Sales Trend">
                    {reportData.sales_trend && reportData.sales_trend.length > 0 ? (
                      <PrintBarChart data={reportData.sales_trend} />
                    ) : (
                      <Typography color="text.secondary">No sales trend data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Weekly Sales Trend">
                    {reportData.weekly_trend && reportData.weekly_trend.length > 0 ? (
                      <PrintBarChart data={reportData.weekly_trend} />
                    ) : (
                      <Typography color="text.secondary">No weekly trend data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Monthly Sales Trend">
                    {reportData.monthly_trend && reportData.monthly_trend.length > 0 ? (
                      <PrintBarChart data={reportData.monthly_trend} />
                    ) : (
                      <Typography color="text.secondary">No monthly trend data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Sales by Category">
                    {reportData.revenue_by_service_category && reportData.revenue_by_service_category.length > 0 ? (
                      <PrintDonutChart data={reportData.revenue_by_service_category} />
                    ) : (
                      <Typography color="text.secondary">No category data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Sales by Payment Method">
                    {reportData.sales_by_payment_method && reportData.sales_by_payment_method.length > 0 ? (
                      <PrintDonutChart data={reportData.sales_by_payment_method} valueKey="total_revenue" labelKey="payment_method" />
                    ) : (
                      <Typography color="text.secondary">No payment method data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Sales by Branch">
                    {reportData.sales_by_branch && reportData.sales_by_branch.length > 0 ? (
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Branch</TableCell>
                              <TableCell align="right">Transactions</TableCell>
                              <TableCell align="right">Revenue</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportData.sales_by_branch.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.branch}</TableCell>
                                <TableCell align="right">{row.transaction_count}</TableCell>
                                <TableCell align="right">{formatCurrency(row.total_revenue)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No branch data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Sales by Employee">
                    {reportData.top_nutritionists && reportData.top_nutritionists.length > 0 ? (
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell align="right">Transactions</TableCell>
                              <TableCell align="right">Revenue</TableCell>
                              <TableCell align="right">Avg Sale</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportData.top_nutritionists.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.full_name}</TableCell>
                                <TableCell align="right">{row.total_transactions}</TableCell>
                                <TableCell align="right">{formatCurrency(row.total_revenue)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.total_revenue / (row.total_transactions || 1))}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No employee data available</Typography>
                    )}
                  </PrintChartContainer>
                </Box>
              )}

              {/* Inventory Section */}
              {reportType === 'inventory' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Inventory Analysis
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <PrintChartContainer title="Current Stock">
                    {reportData.inventory_register && reportData.inventory_register.length > 0 ? (
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>SKU</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell align="right">Stock</TableCell>
                              <TableCell align="right">Min Stock</TableCell>
                              <TableCell align="right">Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportData.inventory_register.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.sku}</TableCell>
                                <TableCell>{row.category}</TableCell>
                                <TableCell align="right">{row.quantity_in_stock}</TableCell>
                                <TableCell align="right">{row.min_stock_level}</TableCell>
                                <TableCell align="right">{formatCurrency(row.selling_price * row.quantity_in_stock)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No inventory data available</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Low Stock Items">
                    {reportData.low_stock_items && reportData.low_stock_items.length > 0 ? (
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Product</TableCell>
                              <TableCell>SKU</TableCell>
                              <TableCell align="right">Current</TableCell>
                              <TableCell align="right">Min</TableCell>
                              <TableCell align="right">Shortage</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportData.low_stock_items.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.sku}</TableCell>
                                <TableCell align="right">{row.quantity_in_stock}</TableCell>
                                <TableCell align="right">{row.min_stock_level}</TableCell>
                                <TableCell align="right">{row.shortage}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No low stock items</Typography>
                    )}
                  </PrintChartContainer>

                  <PrintChartContainer title="Stock Adjustments">
                    {reportData.stock_adjustments && reportData.stock_adjustments.length > 0 ? (
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Product</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell align="right">Quantity</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportData.stock_adjustments.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{formatDate(row.date)}</TableCell>
                                <TableCell>{row.product_name}</TableCell>
                                <TableCell>{row.adjustment_type}</TableCell>
                                <TableCell align="right">{row.quantity}</TableCell>
                                <TableCell>{row.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No stock adjustments recorded</Typography>
                    )}
                  </PrintChartContainer>
                </Box>
              )}

              {/* Financial Summary Section */}
              {reportType === 'financial' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Financial Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Amount (KES)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Revenue</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.summary?.total_revenue)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Cost of Goods Sold</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.summary?.cogs || 0)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'primary.50' }}>
                          <TableCell fontWeight="bold">Gross Profit</TableCell>
                          <TableCell align="right" fontWeight="bold">{formatCurrency(reportData.summary?.gross_profit || 0)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Operating Expenses</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.summary?.operating_expenses || 0)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'primary.50' }}>
                          <TableCell fontWeight="bold">Net Profit</TableCell>
                          <TableCell align="right" fontWeight="bold">{formatCurrency(reportData.summary?.net_profit || 0)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Profit Margin</TableCell>
                          <TableCell align="right">{reportData.summary?.profit_margin ? `${Number(reportData.summary.profit_margin).toFixed(1)}%` : '0%'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>VAT Collected</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.summary?.vat_collected || 0)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Refunds</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.summary?.refunds || 0)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discounts</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.summary?.discounts || 0)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Client List Section */}
              {reportType === 'clients' && reportData?.clients && reportData.clients.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Client List
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Client ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Conditions</TableCell>
                          <TableCell>Last Updated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.clients.map((row, idx) => (
                          <TableRow key={row.id || idx}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.first_name} {row.last_name}</TableCell>
                            <TableCell>{row.phone || 'N/A'}</TableCell>
                            <TableCell>{row.email || 'N/A'}</TableCell>
                            <TableCell>{(row.medical_conditions || []).length ? (row.medical_conditions || []).join(', ') : 'None'}</TableCell>
                            <TableCell>{formatDate(row.updated_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Detailed Sales Table */}
              {reportType === 'sales' && reportData.detailed_sales && reportData.detailed_sales.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Detailed Sales Transactions
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Invoice #</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Products</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell>Payment Method</TableCell>
                          <TableCell>Cashier</TableCell>
                          <TableCell align="right">Discount</TableCell>
                          <TableCell align="right">Tax</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.detailed_sales.map((row, idx) => (
                          <TableRow key={row.receipt_number || idx}>
                            <TableCell>{formatDate(row.sale_date)}</TableCell>
                            <TableCell>{row.receipt_number}</TableCell>
                            <TableCell>{row.client_name || 'Walk-in'}</TableCell>
                            <TableCell>{Array.isArray(row.items) ? row.items.map(i => i.name || i.product_name).join(', ') : ''}</TableCell>
                            <TableCell align="right">{Array.isArray(row.items) ? row.items.length : 0}</TableCell>
                            <TableCell>{row.payment_method || '—'}</TableCell>
                            <TableCell>{row.cashier_name || '—'}</TableCell>
                            <TableCell align="right">{formatCurrency(row.discount)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.tax)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Customer Analysis Section */}
              {reportType === 'sales' && reportData.customer_analysis && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Customer Analysis
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Metric</TableCell>
                          <TableCell align="right">Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>New Customers</TableCell>
                          <TableCell align="right">{(reportData.customer_analysis.new_customers || 0).toLocaleString()}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Returning Customers</TableCell>
                          <TableCell align="right">{(reportData.customer_analysis.returning_customers || 0).toLocaleString()}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Customer Lifetime Value</TableCell>
                          <TableCell align="right">{formatCurrency(reportData.customer_analysis.avg_lifetime_value)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Employee Performance Section */}
              {reportType === 'sales' && reportData.top_nutritionists && reportData.top_nutritionists.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Employee Performance
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell align="right">Sales</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">Avg Sale</TableCell>
                          <TableCell align="right">Commission</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.top_nutritionists.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.full_name}</TableCell>
                            <TableCell align="right">{row.total_transactions}</TableCell>
                            <TableCell align="right">{formatCurrency(row.total_revenue)}</TableCell>
                            <TableCell align="right">{formatCurrency(row.total_revenue / (row.total_transactions || 1))}</TableCell>
                            <TableCell align="right">{formatCurrency(row.commission || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          ) : (
            <Typography>Loading report data...</Typography>
          )}
        </main>

        <PrintPageFooter reportType={reportType} />
      </Box>
    </Box>
  );
};

// Print Bar Chart Component
const PrintBarChart = ({ data = [] }) => {
  const values = data.map(d => Number(d.value || d.total_sales || d.total_revenue || 0));
  const maxV = Math.max(1, ...values);
  const labels = data.map(d => d.label || d.date || d.month || '');

  const barWidth = 30;
  const barGap = 10;
  const chartWidth = Math.max(400, (barWidth + barGap) * data.length);
  const chartHeight = 180;
  const topPadding = 20;
  const bottomPadding = 40;
  const leftPadding = 30;
  const plotHeight = chartHeight - topPadding - bottomPadding;

  return (
    <Box sx={{ width: '100%' }}>
      <svg 
        viewBox={`0 0 ${chartWidth} ${chartHeight}`} 
        style={{ width: '100%', height: '180px' }}
        preserveAspectRatio="none"
      >
        {/* Y-axis line */}
        <line 
          x1={leftPadding} 
          y1={topPadding} 
          x2={leftPadding} 
          y2={chartHeight - bottomPadding} 
          stroke="#E5E7EB" 
          strokeWidth="1" 
        />
        
        {/* Bars */}
        {data.map((d, i) => {
          const value = Number(d.value || d.total_sales || d.total_revenue || 0);
          const barHeight = (value / maxV) * plotHeight;
          const x = leftPadding + i * (barWidth + barGap);
          const y = chartHeight - bottomPadding - barHeight;
          
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, 2)}
                fill="#1976d2"
                rx="2"
              />
              <text
                x={x + barWidth / 2}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize="8"
                fill="#6B7280"
              >
                {labels[i]}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

// Print Donut Chart Component
const PrintDonutChart = ({ data = [], valueKey = 'total_revenue', labelKey = 'category' }) => {
  const total = data.reduce((s, x) => s + Number(x[valueKey] || 0), 0) || 1;
  const colors = ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#388e3c', '#7b1fa2'];
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  let acc = 0;

  const labelPos = (frac, midFrac) => {
    const midAngle = (frac - midFrac / 2) * 360 - 90;
    const rad = (midAngle * Math.PI) / 180;
    const labelRadius = radius + 16;
    return {
      x: 70 + labelRadius * Math.cos(rad),
      y: 70 + labelRadius * Math.sin(rad),
    };
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <svg width="200" height="160" viewBox="0 0 180 160">
        <circle cx="70" cy="70" r={radius} stroke="#E5E7EB" strokeWidth="12" fill="none" />
        {data.map((s, i) => {
          const value = Number(s[valueKey] || 0);
          const pct = value / total;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const midFrac = pct;
          const pos = labelPos(acc + pct, midFrac);
          const labelPct = (pct * 100).toFixed(1);
          const showLabel = pct > 0.05;
          const offset = -acc * circumference;
          acc += pct;

          return (
            <g key={s[labelKey] || i}>
              <circle
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                stroke={colors[i % colors.length]}
                strokeWidth="12"
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 70 70)"
              />
              {showLabel && (
                <>
                  <text x={pos.x} y={pos.y - 4} textAnchor="middle" fontSize="7" fill={colors[i % colors.length]} fontWeight="600">
                    {s[labelKey]}
                  </text>
                  <text x={pos.x} y={pos.y + 8} textAnchor="middle" fontSize="7" fill="#6B7280">
                    {labelPct}%
                  </text>
                </>
              )}
            </g>
          );
        })}
        <text x="70" y="66" textAnchor="middle" dominantBaseline="central" fontSize="8" fill="#6B7280">Total</text>
        <text x="70" y="85" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1F2937">
          {formatCurrency(total)}
        </text>
      </svg>
    </Box>
  );
};

export default ReportPrintLayout;